import { useMemo, useState } from 'react'
import {
  Button,
  CircularProgress,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ApexOptions } from 'apexcharts'

import { ApexChart } from 'ApexChart'
import { strings } from 'strings'
import { AutoStatsDevice, useAutoStatsDevices } from 'api'

import { useAnalyticsRange } from './analyticsShared'
import { UNKNOWN_PROVINCE, useProvinces } from './provinces'
import { BucketDetailsDialog } from './BucketDetailsDialog'

const EPOCH_ISO = '2000-01-01T00:00:00Z'
const DEVICE_COLOR = '#198D10'

interface ProvinceRow {
  name: string
  count: number
  serials: string[]
}

export function ProvinceBasesGrowthBarChart() {
  const classes = useStyles()
  const { allTimeToIso } = useAnalyticsRange()
  const title = strings.numberOfBasesByProvince
  const csvPrefix = 'devices-growth'

  const [selected, setSelected] = useState<string | null>(null)
  const [listOpen, setListOpen] = useState(false)

  const { devices, loading: devicesLoading, error } = useAutoStatsDevices(
    EPOCH_ISO,
    allTimeToIso,
  )
  const { ready: provincesReady, resolve } = useProvinces()
  const loading = devicesLoading || !provincesReady

  const serialToProvince = useMemo(() => {
    const m = new Map<string, string>()
    if (!provincesReady || !devices) return m
    for (const d of devices as AutoStatsDevice[]) {
      const p =
        d.latitude === 0 && d.longitude === 0
          ? UNKNOWN_PROVINCE
          : (resolve(d.latitude, d.longitude) ?? UNKNOWN_PROVINCE)
      m.set(d.serial, p)
    }
    return m
  }, [devices, provincesReady, resolve])

  const provinceRows: ProvinceRow[] = useMemo(() => {
    const counts = new Map<string, string[]>()
    serialToProvince.forEach((p, serial) => {
      const arr = counts.get(p) ?? []
      arr.push(serial)
      counts.set(p, arr)
    })
    const rows: ProvinceRow[] = []
    counts.forEach((serials, name) =>
      rows.push({ name, count: serials.length, serials }),
    )
    rows.sort((a, b) => b.count - a.count)
    return rows
  }, [serialToProvince])

  const isEmpty =
    !loading && provinceRows.every((r) => r.count === 0)

  const provinceLabelFor = (name: string) =>
    name === UNKNOWN_PROVINCE ? strings.unknownProvince : name

  const options: ApexOptions | null = useMemo(() => {
    if (loading || isEmpty || provinceRows.length === 0) return null
    return {
      series: [
        { name: strings.devices, data: provinceRows.map((r) => r.count) },
      ],
      chart: {
        type: 'bar',
        fontFamily: 'vazir',
        height: 420,
        width: '100%',
        animations: { enabled: false },
        toolbar: { show: true },
        events: {
          dataPointSelection: (
            _e,
            _ctx,
            cfg: { dataPointIndex?: number },
          ) => {
            const pi = cfg?.dataPointIndex ?? -1
            const row = provinceRows[pi]
            if (!row || row.count <= 0) return
            setSelected(row.name)
          },
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '70%',
          dataLabels: { position: 'top' },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) =>
          (val as number) > 0 ? String(Math.round(val as number)) : '',
        offsetY: -8,
        style: { fontSize: '11px', colors: ['#333'] },
      },
      xaxis: {
        categories: provinceRows.map((r) => provinceLabelFor(r.name)),
        labels: { rotate: -45, style: { fontSize: '11px' } },
      },
      yaxis: {
        title: { text: strings.cumulativeCount },
        labels: { formatter: (val) => String(Math.round(Number(val))) },
      },
      legend: { position: 'top', horizontalAlign: 'left' },
      colors: [DEVICE_COLOR],
      tooltip: {
        y: { formatter: (val) => String(Math.round(val as number)) },
      },
    }
  }, [loading, isEmpty, provinceRows])

  const selectedAllowlist = useMemo(() => {
    if (selected === null) return null
    return provinceRows.find((r) => r.name === selected)?.serials ?? []
  }, [selected, provinceRows])

  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <Typography className={classes.title}>{title}</Typography>
        <div className={classes.controls}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setListOpen(true)}
          >
            {strings.showAllDevices}
          </Button>
        </div>
      </div>
      <div className={classes.barChartWrap}>
        {error && (
          <Typography className={classes.status}>
            {strings.error}: {String((error as any)?.message ?? error)}
          </Typography>
        )}
        {!error && loading && (
          <div className={classes.status}>
            <CircularProgress size={24} />
            <Typography>{strings.loading}</Typography>
          </div>
        )}
        {!error && !loading && isEmpty && (
          <Typography className={classes.status}>
            {strings.noGrowthInRange}
          </Typography>
        )}
        {!error && options && (
          <ApexChart options={options} className={classes.chart} />
        )}
      </div>
      <BucketDetailsDialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={
          selected
            ? `${strings.devices} — ${provinceLabelFor(selected)}`
            : title
        }
        kind="devices"
        from={EPOCH_ISO}
        to={allTimeToIso}
        csvFilename={selected ? `${csvPrefix}-${selected}` : csvPrefix}
        deviceSerialAllowlist={selectedAllowlist}
      />
      <BucketDetailsDialog
        open={listOpen}
        onClose={() => setListOpen(false)}
        title={strings.allDevices}
        kind="devices"
        from={EPOCH_ISO}
        to={allTimeToIso}
        csvFilename={csvPrefix}
      />
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  card: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    overflow: 'hidden',
    width: '100%',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    background: 'silver',
    flexWrap: 'wrap',
  },
  title: {
    fontWeight: 600,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  barChartWrap: {
    width: '100%',
    height: 460,
  },
  chart: {
    width: '100%',
    height: '100%',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    height: 360,
    justifyContent: 'center',
  },
}))
